import { useState, useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAssignmentById } from "@/lib/assignment";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  Clock,
  Users,
  AlertTriangle,
  FileText,
  CheckCircle,
  XCircle,
  PlayCircle,
  PauseCircle,
  Edit,
  Trash2,
  Plus,
  MessageSquare,
  Download,
  Eye,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  Target,
  Shield,
  BarChart3,
  Activity,
  MoreVertical,
  X,
  Search,
  Filter,
  Star,
  CheckCircle2,
  AlertCircle,
  Settings,
  Zap,
  FlaskConical,
} from "lucide-react";
import RiskSelectionModal from "@/components/assignments/risk-selection-modal";
import ControlSelectionModal from "@/components/assignments/control-selection-modal";
import TestControlDialog from "@/components/controls/test-control-dialog";
import ViewTestDetailsModal from "@/components/controls/view-test-details-modal";
import { useToast } from "@/hooks/use-toast";
import { OverviewTab } from "./_components/tabs/OverviewTab";
import { TasksTab } from "./_components/tabs/TasksTab";
import { RisksControlsTab } from "./_components/tabs/RisksControlsTab";
import { DocumentsTab } from "./_components/tabs/DocumentsTab";
import type {
  Assignment,
  AuditTask,
  DocumentFile,
  RelatedRiskEntry,
  UserStub,
} from "./_types/assignment-types";

// Default empty data structures
const defaultTasks: AuditTask[] = [];
const defaultRelatedRisks: RelatedRiskEntry[] = [];
const defaultDocuments: DocumentFile[] = [];

export default function AssignmentDetailPage() {
  const { assignmentId } = useParams({ from: '/assignments/$assignmentId' });
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<AuditTask[]>(defaultTasks);
  const [relatedRisksData, setRelatedRisksData] =
    useState<RelatedRiskEntry[]>(defaultRelatedRisks);
  const [documents, setDocuments] = useState<DocumentFile[]>(defaultDocuments);
  const { toast } = useToast();
  const [availableUsers, setAvailableUsers] = useState<UserStub[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [teamMembers, setTeamMembers] = useState<UserStub[]>([]);

  const {
    isPending: isLoading,
    isError,
    error: queryError,
    data: assignmentData,
  } = useQuery({
    queryKey: ["assignments", assignmentId],
    queryFn: async () => {
      if (!assignmentId) {
        throw new Error("Assignment ID is required");
      }
      return getAssignmentById(assignmentId);
    },
    enabled: !!assignmentId,
  });
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] =
    useState(false);
  const [newTaskData, setNewTaskData] = useState({
    description: "",
    assigneeId: "",
    dueDate: null as Date | null,
    status: "Pending",
  });
  const [newTeamMemberData, setNewTeamMemberData] = useState({
    userId: "",
    assignmentRole: "Team Member",
  });

  // Risk and Control state - Using a different approach
  const [showAddSection, setShowAddSection] = useState(false);
  const [isControlSelectionModalOpen, setIsControlSelectionModalOpen] =
    useState(false);
  const [selectedRiskIds, setSelectedRiskIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(
    new Set(),
  );

  // Data states for risk and control selection - cached for performance
  const [availableRisks, setAvailableRisks] = useState<any[]>([]);
  const [availableControls, setAvailableControls] = useState<any[]>([]);
  const [loadingRisks, setLoadingRisks] = useState(false);
  const [loadingControls, setLoadingControls] = useState(false);
  const [risksDataLoaded, setRisksDataLoaded] = useState(false);
  const [controlsDataLoaded, setControlsDataLoaded] = useState(false);

  // Search and filter states for enhanced UX
  const [riskSearchTerm, setRiskSearchTerm] = useState("");
  const [controlSearchTerm, setControlSearchTerm] = useState("");
  const [selectedRiskLevelFilter, setSelectedRiskLevelFilter] = useState("All");
  const [selectedControlTypeFilter, setSelectedControlTypeFilter] =
    useState("All");

  // Advanced filtering states
  const [selectedRiskCategoryFilter, setSelectedRiskCategoryFilter] =
    useState("All");
  const [
    selectedControlEffectivenessFilter,
    setSelectedControlEffectivenessFilter,
  ] = useState("All");
  const [showOnlyUnlinked, setShowOnlyUnlinked] = useState(false);
  const [quickFilterMode, setQuickFilterMode] = useState<
    "all" | "critical" | "recommended"
  >("all");

  const [isAddingRiskControl, setIsAddingRiskControl] = useState(false);

  // Test Control Dialog state
  const [isTestControlDialogOpen, setIsTestControlDialogOpen] = useState(false);
  const [selectedControlForTest, setSelectedControlForTest] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [controlTests, setControlTests] = useState<Record<string, any[]>>({});
  const [isViewTestDetailsOpen, setIsViewTestDetailsOpen] = useState(false);
  const [selectedTestForView, setSelectedTestForView] = useState<any>(null);
  const [isEditTestMode, setIsEditTestMode] = useState(false);
  const [selectedTestForEdit, setSelectedTestForEdit] = useState<any>(null);

  // Reset modal states on component mount - only when assignmentId changes
  useEffect(() => {
    setShowAddSection(false);
    setIsControlSelectionModalOpen(false);
  }, [assignmentId]);

  // Calculate progress - reactive to task status changes
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(
    (task) => task.status === "Completed",
  ).length;
  // Use completionPercentage from API if available, otherwise calculate from tasks
  const progressPercentage =
    assignmentData?.completionPercentage ??
    (totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0);

  // Fetch related data when assignment is loaded
  useEffect(() => {
    if (!assignmentId || !assignmentData) return;

    const fetchRelatedData = async () => {
      await Promise.all([
        fetchTasks(assignmentId),
        fetchRisksAndControls(assignmentId),
        fetchTeamMembers(assignmentId),
        fetchDocuments(assignmentId),
      ]);

      // Preload risks and controls data for modal performance
      setTimeout(() => {
        Promise.all([fetchRisks(), fetchControls()]).catch((error) => {
          console.log("Preload failed but not critical:", error);
        });
      }, 1000);
    };

    fetchRelatedData();
  }, [assignmentId, assignmentData]);

  // Helper functions to fetch real data
  const fetchTasks = async (assignmentId: string) => {
    try {
      const response = await fetch(`/api/assignments/${assignmentId}/tasks`);

      if (response.ok) {
        const tasksData = await response.json();
        setTasks(tasksData || []);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    }
  };
  const fetchDocuments = async (assignmentId: string) => {
    try {
      const res = await fetch(`/api/assignments/${assignmentId}/documents`, {
        cache: "no-store",
      });
      if (!res.ok) {
        setDocuments([]);
        return;
      }
      const data = await res.json();
      const mapped: DocumentFile[] = (data || []).map((d: any) => ({
        id: String(d.DocumentID),
        name: d.DocumentName,
        type: d.DocumentFileType || "Unknown",
        size: d.DocumentFileSize
          ? `${Math.round(d.DocumentFileSize / 1024)} KB`
          : "—",
        uploadDate: d.CreatedDate
          ? new Date(d.CreatedDate).toLocaleDateString()
          : "",
        uploader: "",
      }));
      setDocuments(mapped);
    } catch (e) {
      console.error("Failed to load documents", e);
      setDocuments([]);
    }
  };

  const handleUploadDocument = async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = false;
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        // Step 1: upload to temp
        const form = new FormData();
        form.append("file", file);
        const tmpRes = await fetch("/api/files", {
          method: "POST",
          body: form,
        });
        const tmp = await tmpRes.json();
        if (!tmpRes.ok || !tmp?.ok) {
          toast({
            title: "Upload failed",
            description: tmp?.error || "Could not upload file",
            variant: "destructive",
          });
          return;
        }

        // Step 2: commit to assignment docs
        const commitRes = await fetch(
          `/api/assignments/${assignmentId}/documents`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tempPath: tmp.tempPath,
              originalName: tmp.fileName,
              mime: tmp.mime,
              size: tmp.size,
            }),
          },
        );

        if (!commitRes.ok) {
          const j = await commitRes.json().catch(() => ({}) as any);
          toast({
            title: "Save failed",
            description: j?.message || "Could not save document",
            variant: "destructive",
          });
          return;
        }

        toast({ title: "Document uploaded", description: file.name });
        await fetchDocuments(assignmentId);
      };
      input.click();
    } catch (e) {
      console.error("Upload failed", e);
      toast({ title: "Upload failed", variant: "destructive" });
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm("Delete this document?")) return;
    const res = await fetch(
      `/api/assignments/${assignmentId}/documents?documentId=${documentId}`,
      { method: "DELETE" },
    );
    if (res.ok) {
      toast({ title: "Document deleted" });
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
    } else {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const fetchRisksAndControls = async (assignmentId: string) => {
    try {
      const response = await fetch(
        `/api/assignments/${assignmentId}/risk-controls`,
      );
      if (response.ok) {
        const risksData = await response.json();
        setRelatedRisksData(risksData || []);
      } else {
        setRelatedRisksData([]);
      }
    } catch (error) {
      console.error("Error fetching risk-controls:", error);
      setRelatedRisksData([]);
    }
  };

  const fetchTeamMembers = async (id: string) => {
    try {
      const response = await fetch(`/api/assignments/${id}/team`);
      if (response.ok) {
        const teamData = await response.json();
        console.log("Fetched team members:", teamData);
        setTeamMembers(teamData || []);
      } else {
        console.warn("Failed to fetch team members, using empty array");
        setTeamMembers([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
    }
  };

  // Fetch available users for assignment (for adding new team members)
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await fetch("/api/users");
        if (!response.ok) {
          throw new Error(`Failed to fetch users: ${response.status}`);
        }
        const data: UserStub[] = await response.json();
        const usersWithAvatars = data.map((user) => ({
          ...user,
          avatar: "/placeholder.svg?width=40&height=40",
        }));
        setAvailableUsers(usersWithAvatars);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (assignmentData) {
      fetchUsers();
    }
  }, [assignmentData]);

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleAddTask = async (taskData: Partial<AuditTask>) => {
    if (!assignmentId) return;

    try {
      const response = await fetch(`/api/assignments/${assignmentId}/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          description: taskData.description,
          status: taskData.status || "Pending",
          assigneeId: taskData.assigneeId,
          dueDate: taskData.dueDate,
        }),
      });

      if (response.ok) {
        const newTask = await response.json();
        setTasks((prev) => [...prev, newTask]);
        // Refresh tasks to get the latest data
        await fetchTasks(assignmentId);
      } else {
        console.error("Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
    }
  };

  const handleAddTeamMember = async (teamMemberData: {
    userId: string;
    assignmentRole: string;
  }) => {
    if (!assignmentId) return;

    try {
      const response = await fetch(`/api/assignments/${assignmentId}/team`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(teamMemberData),
      });

      if (response.ok) {
        // Refresh team members to get the latest data
        await fetchTeamMembers(assignmentId);
      } else {
        console.error("Failed to add team member");
      }
    } catch (error) {
      console.error("Error adding team member:", error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this task? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `/api/assignments/${assignmentId}/tasks?taskId=${taskId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete task");
      }

      // Remove the task from local state
      setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("Failed to delete task. Please try again.");
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: string) => {
    try {
      const response = await fetch(
        `/api/assignments/${assignmentId}/tasks?taskId=${taskId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update task status");
      }

      const updatedTask = await response.json();

      // Update the task in local state
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId
            ? { ...task, status: newStatus as AuditTask["status"] }
            : task,
        ),
      );
    } catch (error) {
      console.error("Error updating task status:", error);
      alert("Failed to update task status. Please try again.");
    }
  };

  // Fetch available risks (cached for performance)
  const fetchRisks = async () => {
    if (risksDataLoaded) {
      return;
    }

    setLoadingRisks(true);
    try {
      const response = await fetch("/api/risks");
      if (response.ok) {
        const risks = await response.json();
        setAvailableRisks(risks);
        setRisksDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching risks:", error);
    } finally {
      setLoadingRisks(false);
    }
  };

  // Fetch available controls (cached for performance)
  const fetchControls = async () => {
    if (controlsDataLoaded) {
      return;
    }

    setLoadingControls(true);
    try {
      const response = await fetch("/api/controls");
      if (response.ok) {
        const controls = await response.json();
        setAvailableControls(controls);
        setControlsDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching controls:", error);
    } finally {
      setLoadingControls(false);
    }
  };

  // Helper function to clean up expandable section state
  const closeAddSection = () => {
    setShowAddSection(false);
    setSelectedRiskIds(new Set());
    setSelectedControlIds(new Set());
    // Reset search/filter states
    setRiskSearchTerm("");
    setControlSearchTerm("");
    setSelectedRiskLevelFilter("All");
    setSelectedControlTypeFilter("All");
    setSelectedRiskCategoryFilter("All");
    setSelectedControlEffectivenessFilter("All");
    setShowOnlyUnlinked(false);
    setQuickFilterMode("all");
  };

  // Smart filtering functions for enhanced UX
  const getFilteredRisks = () => {
    let filtered = availableRisks.filter((risk) => {
      // Search filter (title and description)
      const matchesSearch =
        riskSearchTerm === "" ||
        risk.title?.toLowerCase().includes(riskSearchTerm.toLowerCase()) ||
        risk.description?.toLowerCase().includes(riskSearchTerm.toLowerCase());

      // Risk level filter
      const riskLevel = risk.riskLevel || risk.inherentRisk || "Medium";
      const matchesLevel =
        selectedRiskLevelFilter === "All" ||
        riskLevel.toLowerCase() === selectedRiskLevelFilter.toLowerCase();

      // Risk category filter (process/function)
      const riskCategory = risk.process || risk.category || "General";
      const matchesCategory =
        selectedRiskCategoryFilter === "All" ||
        riskCategory
          .toLowerCase()
          .includes(selectedRiskCategoryFilter.toLowerCase());

      // Show only unlinked filter
      const isAlreadyLinked = relatedRisksData.some(
        (riskEntry) =>
          riskEntry.risk.id === risk.id ||
          riskEntry.risk.id === risk.id.toString(),
      );
      const matchesUnlinked = !showOnlyUnlinked || !isAlreadyLinked;

      return (
        matchesSearch && matchesLevel && matchesCategory && matchesUnlinked
      );
    });

    // Apply quick filter modes
    if (quickFilterMode === "critical") {
      filtered = filtered.filter((risk) => {
        const riskLevel = (
          risk.riskLevel ||
          risk.inherentRisk ||
          "Medium"
        ).toLowerCase();
        return riskLevel === "critical" || riskLevel === "high";
      });
    } else if (quickFilterMode === "recommended") {
      // Show risks that are commonly used or have high priority
      filtered = filtered.filter((risk) => {
        const riskLevel = (
          risk.riskLevel ||
          risk.inherentRisk ||
          "Medium"
        ).toLowerCase();
        return riskLevel === "high" || riskLevel === "medium";
      });
    }

    return filtered;
  };

  const getFilteredControls = () => {
    let filtered = availableControls.filter((control) => {
      // Search filter (name and description)
      const matchesSearch =
        controlSearchTerm === "" ||
        control.name?.toLowerCase().includes(controlSearchTerm.toLowerCase()) ||
        control.description
          ?.toLowerCase()
          .includes(controlSearchTerm.toLowerCase());

      // Control type filter
      const controlType = control.type || control.typeName || "Control";
      const matchesType =
        selectedControlTypeFilter === "All" ||
        controlType
          .toLowerCase()
          .includes(selectedControlTypeFilter.toLowerCase());

      // Control effectiveness filter
      const effectiveness = control.effectiveness || "Effective";
      const matchesEffectiveness =
        selectedControlEffectivenessFilter === "All" ||
        effectiveness
          .toLowerCase()
          .includes(selectedControlEffectivenessFilter.toLowerCase());

      // Show only unlinked filter
      const isAlreadyLinked = relatedRisksData.some((riskEntry) =>
        riskEntry.controls.some(
          (ctrl) => ctrl.id === control.id || ctrl.id === control.id.toString(),
        ),
      );
      const matchesUnlinked = !showOnlyUnlinked || !isAlreadyLinked;

      return (
        matchesSearch && matchesType && matchesEffectiveness && matchesUnlinked
      );
    });

    // Apply quick filter modes for controls
    if (quickFilterMode === "critical") {
      filtered = filtered.filter((control) => {
        const type = (control.type || control.typeName || "").toLowerCase();
        return type.includes("preventive") || type.includes("detective");
      });
    } else if (quickFilterMode === "recommended") {
      filtered = filtered.filter((control) => {
        const effectiveness = (control.effectiveness || "").toLowerCase();
        return (
          effectiveness.includes("effective") ||
          effectiveness.includes("adequate")
        );
      });
    }

    return filtered;
  };

  // Get unique risk levels and control types for filter buttons
  const getRiskLevels = () => {
    const levels = new Set(
      availableRisks.map(
        (risk) => risk.riskLevel || risk.inherentRisk || "Medium",
      ),
    );
    return ["All", ...Array.from(levels)];
  };

  const getControlTypes = () => {
    const types = new Set(
      availableControls.map(
        (control) => control.type || control.typeName || "Control",
      ),
    );
    return ["All", ...Array.from(types)];
  };

  const getRiskCategories = () => {
    const categories = new Set(
      availableRisks.map((risk) => risk.process || risk.category || "General"),
    );
    return ["All", ...Array.from(categories)];
  };

  const getControlEffectiveness = () => {
    const effectiveness = new Set(
      availableControls.map((control) => control.effectiveness || "Effective"),
    );
    return ["All", ...Array.from(effectiveness)];
  };

  const handleAddRiskControl = async () => {
    // Check if user is on the correct tab
    if (activeTab !== "risks") {
      setActiveTab("risks");
      toast({
        title: "Switched to Risks & Controls Tab",
        description: "Opening the Risk & Control selection section...",
      });
      // Small delay to let tab switch complete
      setTimeout(() => {
        handleAddRiskControl(); // Recursively call after tab switch
      }, 300);
      return;
    }

    try {
      // Clear any previous selections to ensure clean state
      setSelectedRiskIds(new Set());
      setSelectedControlIds(new Set());

      // Toggle the expandable section
      setShowAddSection(!showAddSection);

      // If opening the section, fetch data (using cache if available)
      if (!showAddSection) {
        await Promise.all([fetchRisks(), fetchControls()]);
      }
    } catch (error) {
      console.error("Error opening add section:", error);
      closeAddSection(); // Clean close on error
      toast({
        title: "Error",
        description: "Failed to open risk selection section. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRiskSelectionConfirm = async (selectedRisks: any[]) => {
    setSelectedRiskIds(new Set(selectedRisks.map((r) => r.id)));
    setShowAddSection(false);

    if (selectedRisks.length > 0) {
      setIsControlSelectionModalOpen(true);
    }
  };

  const handleControlSelectionConfirm = async (selectedControls: any[]) => {
    setSelectedControlIds(new Set(selectedControls.map((c) => c.id)));
    setIsControlSelectionModalOpen(false);

    // Now save the risk-control relationships
    await saveRiskControlRelationships(
      Array.from(selectedRiskIds),
      selectedControls.map((c) => c.id),
    );
  };

  const saveRiskControlRelationships = async (
    riskIds: string[],
    controlIds: string[],
  ) => {
    try {
      setIsAddingRiskControl(true);

      const response = await fetch(
        `/api/assignments/${assignmentId}/risk-controls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            riskIds,
            controlIds,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save risk-control relationships");
      }

      // Refresh the assignment data to show new risks and controls
      await fetchRisksAndControls(assignmentId);

      // Reset selection state
      setSelectedRiskIds(new Set());
      setSelectedControlIds(new Set());

      toast({
        title: "Success",
        description: `Successfully linked ${riskIds.length} risk(s) and ${controlIds.length} control(s) to this assignment.`,
      });
    } catch (error) {
      console.error("Error saving risk-control relationships:", error);
      toast({
        title: "Error",
        description:
          "Failed to save risk-control relationships. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingRiskControl(false);
    }
  };

  const handleRemoveRisk = async (riskId: string) => {
    // Validate risk ID format
    if (!riskId || isNaN(Number(riskId))) {
      toast({
        title: "Error",
        description: "Invalid risk ID format.",
        variant: "destructive",
      });
      return;
    }

    if (
      !confirm(
        "Are you sure you want to remove this risk from the assignment? This will also remove all associated controls.",
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `/api/assignments/${assignmentId}/risk-controls?riskId=${riskId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove risk");
      }

      await response.json();

      // Refresh the assignment data
      await fetchRisksAndControls(assignmentId);

      toast({
        title: "Success",
        description: "Risk removed from assignment successfully.",
      });
    } catch (error) {
      console.error("Error removing risk:", error);
      toast({
        title: "Error",
        description: "Failed to remove risk. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveControl = async (riskId: string, controlId: string) => {
    // Validate ID formats
    if (
      !riskId ||
      isNaN(Number(riskId)) ||
      !controlId ||
      isNaN(Number(controlId))
    ) {
      toast({
        title: "Error",
        description: "Invalid risk or control ID format.",
        variant: "destructive",
      });
      return;
    }

    if (
      !confirm("Are you sure you want to remove this control from the risk?")
    ) {
      return;
    }

    try {
      const response = await fetch(
        `/api/assignments/${assignmentId}/risk-controls?riskId=${riskId}&controlId=${controlId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove control");
      }

      await response.json();

      // Refresh the assignment data
      await fetchRisksAndControls(assignmentId);

      toast({
        title: "Success",
        description: "Control removed from risk successfully.",
      });
    } catch (error) {
      console.error("Error removing control:", error);
      toast({
        title: "Error",
        description: "Failed to remove control. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "In Progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Blocked":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "High":
        return "bg-red-100 text-red-800 border-red-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "No date";

    // Convert string to Date if needed
    const dateObj = typeof date === "string" ? new Date(date) : date;

    // Check if it's a valid date
    if (isNaN(dateObj.getTime())) return "Invalid date";

    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDaysRemaining = (date: Date | string | null) => {
    if (!date) return null;

    // Convert string to Date if needed
    const dateObj = typeof date === "string" ? new Date(date) : date;

    // Check if it's a valid date
    if (isNaN(dateObj.getTime())) return null;

    const today = new Date();
    const diffTime = dateObj.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span>Loading assignment details...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="text-red-500 text-xl">⚠️</div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-600">
              Error Loading Assignment
            </h3>
            <p className="text-muted-foreground">
              {queryError instanceof Error
                ? queryError.message
                : "Failed to load assignment data"}
            </p>
          </div>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!assignmentData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Assignment Not Found</h3>
          <p className="text-muted-foreground">
            The requested assignment could not be found.
          </p>
          <Button onClick={() => navigate({ to: "/audit-plans" })} className="mt-4">
            Back to Audit Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="text-muted-foreground hover:text-foreground"
            >
              ← Back
            </Button>
            <Separator orientation="vertical" className="h-4" />
            <span className="text-sm text-muted-foreground">
              Assignment #{assignmentId}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            {assignmentData.name}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-3xl">
            {assignmentData.description || "No description available"}
          </p>
        </div>
        {/*<div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm">
            <MessageSquare className="h-4 w-4 mr-2" />
            Add Comment
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsAddTaskModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>*/}
      </div>

      {/* Progress Overview */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Progress
                </p>
                <p className="text-2xl font-bold">
                  {Math.round(progressPercentage)}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Completed
                </p>
                <p className="text-2xl font-bold">
                  {completedTasks}/{totalTasks}
                </p>
              </div>
            </div>
            {/* Days Remaining - commented out: endDate not available from getAssignmentById */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Days Remaining
                </p>
                <p className="text-2xl font-bold">
                  {getDaysRemaining(assignmentData.endDate)}
                </p>
              </div>
            </div>
            {/* Team Size - commented out: teamMembers not available from getAssignmentById */}
            {/* <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Team Size</p>
                <p className="text-2xl font-bold">{assignmentData.teamMembers.length}</p>
              </div>
            </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="risks">Risks & Controls</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          {/* Timeline tab removed: stages and currentStageIndex not available from getAssignmentById */}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            assignmentData={assignmentData}
            formatDate={formatDate}
          />
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6">
          <TasksTab
            tasks={tasks}
            availableUsers={availableUsers}
            expandedTasks={expandedTasks}
            onAddTaskClick={() => setIsAddTaskModalOpen(true)}
            onToggleTaskExpansion={toggleTaskExpansion}
            onUpdateTaskStatus={handleUpdateTaskStatus}
            onDeleteTask={handleDeleteTask}
            formatDate={formatDate}
            getDaysRemaining={getDaysRemaining}
            getStatusColor={getStatusColor}
          />
        </TabsContent>

        {/* Risks & Controls Tab */}
        <TabsContent value="risks" className="mt-6">
          <RisksControlsTab
            assignmentId={assignmentId}
            relatedRisksData={relatedRisksData}
            showAddSection={showAddSection}
            isAddingRiskControl={isAddingRiskControl}
            loadingRisks={loadingRisks}
            loadingControls={loadingControls}
            availableRisks={availableRisks}
            availableControls={availableControls}
            selectedRiskIds={selectedRiskIds}
            selectedControlIds={selectedControlIds}
            riskSearchTerm={riskSearchTerm}
            controlSearchTerm={controlSearchTerm}
            selectedRiskLevelFilter={selectedRiskLevelFilter}
            selectedControlTypeFilter={selectedControlTypeFilter}
            selectedRiskCategoryFilter={selectedRiskCategoryFilter}
            selectedControlEffectivenessFilter={
              selectedControlEffectivenessFilter
            }
            showOnlyUnlinked={showOnlyUnlinked}
            quickFilterMode={quickFilterMode}
            onAddRiskControlClick={handleAddRiskControl}
            onCloseAddSection={closeAddSection}
            onSetSelectedRiskIds={setSelectedRiskIds}
            onSetSelectedControlIds={setSelectedControlIds}
            onSetRiskSearchTerm={setRiskSearchTerm}
            onSetControlSearchTerm={setControlSearchTerm}
            onSetSelectedRiskLevelFilter={setSelectedRiskLevelFilter}
            onSetSelectedControlTypeFilter={setSelectedControlTypeFilter}
            onSetSelectedRiskCategoryFilter={setSelectedRiskCategoryFilter}
            onSetSelectedControlEffectivenessFilter={
              setSelectedControlEffectivenessFilter
            }
            onSetShowOnlyUnlinked={setShowOnlyUnlinked}
            onSetQuickFilterMode={setQuickFilterMode}
            onRemoveRisk={handleRemoveRisk}
            onRemoveControl={handleRemoveControl}
            onTestControl={(control) => {
              setSelectedControlForTest(control);
              setIsTestControlDialogOpen(true);
            }}
            onViewTestDetails={(testDetails) => {
              setSelectedTestForView(testDetails);
              setIsViewTestDetailsOpen(true);
            }}
            onEditTest={(control, testDetails) => {
              setSelectedControlForTest(control);
              setSelectedTestForEdit(testDetails);
              setIsEditTestMode(true);
              setIsTestControlDialogOpen(true);
            }}
            onSaveRiskControls={async () => {
              await fetchRisksAndControls(assignmentId);
            }}
            getFilteredRisks={getFilteredRisks}
            getFilteredControls={getFilteredControls}
            getRiskLevels={getRiskLevels}
            getControlTypes={getControlTypes}
            getRiskCategories={getRiskCategories}
            getControlEffectiveness={getControlEffectiveness}
            getRiskColor={getRiskColor}
            toast={toast}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <DocumentsTab
            assignmentId={assignmentId}
            documents={documents}
            onUploadDocument={handleUploadDocument}
            onDeleteDocument={handleDeleteDocument}
          />
        </TabsContent>

        {/* Timeline Tab - commented out: stages and currentStageIndex not available from getAssignmentById */}
        {/* <TabsContent value="timeline" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Project Timeline
              </CardTitle>
              <CardDescription>Assignment stages and milestones</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {assignmentData.stages.map((stage, index) => (
                  <div key={`stage-${index}`} className="flex items-center gap-4">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        index < assignmentData.currentStageIndex
                          ? "bg-green-100 text-green-800"
                          : index === assignmentData.currentStageIndex
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {index < assignmentData.currentStageIndex ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className={`font-medium ${
                          index === assignmentData.currentStageIndex
                            ? "text-blue-600"
                            : ""
                        }`}
                      >
                        {stage}
                      </p>
                      {index === assignmentData.currentStageIndex && (
                        <p className="text-sm text-muted-foreground">Current Stage</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Separator className="my-6" />
              <div>
                <h4 className="font-medium mb-4">Next Milestone</h4>
                <p className="text-muted-foreground">
                  Complete {assignmentData.stages[assignmentData.currentStageIndex]} phase
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>

      {/* Add Task Modal */}
      {isAddTaskModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Add New Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Task Description
                </label>
                <input
                  type="text"
                  value={newTaskData.description}
                  onChange={(e) =>
                    setNewTaskData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter task description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Assignee
                </label>
                <select
                  value={newTaskData.assigneeId}
                  onChange={(e) =>
                    setNewTaskData((prev) => ({
                      ...prev,
                      assigneeId: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Unassigned</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={
                    newTaskData.dueDate
                      ? newTaskData.dueDate.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setNewTaskData((prev) => ({
                      ...prev,
                      dueDate: e.target.value ? new Date(e.target.value) : null,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={newTaskData.status}
                  onChange={(e) =>
                    setNewTaskData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setIsAddTaskModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (newTaskData.description.trim()) {
                    await handleAddTask({
                      ...newTaskData,
                      status: newTaskData.status as AuditTask["status"],
                      dueDate: newTaskData.dueDate ?? undefined,
                    });
                    setIsAddTaskModalOpen(false);
                    setNewTaskData({
                      description: "",
                      assigneeId: "",
                      dueDate: null,
                      status: "Pending",
                    });
                  }
                }}
                disabled={!newTaskData.description.trim()}
              >
                Add Task
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {isAddTeamMemberModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Team Member</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select User
                </label>
                <select
                  value={newTeamMemberData.userId}
                  onChange={(e) =>
                    setNewTeamMemberData((prev) => ({
                      ...prev,
                      userId: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Select a user</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Assignment Role
                </label>
                <select
                  value={newTeamMemberData.assignmentRole}
                  onChange={(e) =>
                    setNewTeamMemberData((prev) => ({
                      ...prev,
                      assignmentRole: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="Team Member">Team Member</option>
                  <option value="Lead">Lead</option>
                  <option value="Reviewer">Reviewer</option>
                  <option value="Observer">Observer</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setIsAddTeamMemberModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (newTeamMemberData.userId) {
                    await handleAddTeamMember(newTeamMemberData);
                    setIsAddTeamMemberModalOpen(false);
                    setNewTeamMemberData({
                      userId: "",
                      assignmentRole: "Team Member",
                    });
                  }
                }}
                disabled={!newTeamMemberData.userId}
              >
                Add Member
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Control Selection Modal */}
      <ControlSelectionModal
        isOpen={isControlSelectionModalOpen}
        onClose={() => {
          setIsControlSelectionModalOpen(false);
          setSelectedControlIds(new Set());
        }}
        onConfirm={handleControlSelectionConfirm}
        selectedControlIds={selectedControlIds}
        title="Add Controls to Assignment"
        description="Select controls from the governance model to link to this assignment."
      />

      {/* Test Control Dialog */}
      {selectedControlForTest && (
        <TestControlDialog
          isOpen={isTestControlDialogOpen}
          onClose={() => {
            setIsTestControlDialogOpen(false);
            setSelectedControlForTest(null);
            setIsEditTestMode(false);
            setSelectedTestForEdit(null);
          }}
          controlId={selectedControlForTest.id}
          controlName={selectedControlForTest.name}
          riskId={(selectedControlForTest as any).riskId}
          assignmentId={assignmentId}
          isEditMode={isEditTestMode}
          existingTestData={selectedTestForEdit}
          onTestCreated={async () => {
            // Refetch assignment risk-control data to show updated test status
            await fetchRisksAndControls(assignmentId);
            toast({
              title: isEditTestMode ? "Test Updated" : "Test Recorded",
              description: isEditTestMode
                ? "Control test has been successfully updated."
                : "Control test has been successfully recorded.",
            });
          }}
        />
      )}

      {/* View Test Details Modal */}
      {selectedTestForView && (
        <ViewTestDetailsModal
          isOpen={isViewTestDetailsOpen}
          onClose={() => {
            setIsViewTestDetailsOpen(false);
            setSelectedTestForView(null);
          }}
          controlId={selectedTestForView.controlId}
          controlName={selectedTestForView.controlName}
          riskId={selectedTestForView.riskId}
          assignmentId={assignmentId}
        />
      )}
    </div>
  );
}
